import axios from "axios";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "../utils/toast";
import { useOptions } from "../components/scholarships/model";

export const ACADEMIC_YEARS_KEY = ["academicYears"];

// The code is the starting year ("2025" for 2025-2026)
const newestFirst = (a, b) => Number(b.academicYearCode) - Number(a.academicYearCode);

/**
 * The academic years HEC has created, newest first. By default only up to the current one (the
 * Scholarship Admin sets in Settings when a new year starts), so a screen's first year is the
 * current one; `includeFuture` adds the years ahead, for filters over scholarships planned ahead.
 * The endpoint is public, so this also works before sign-in.
 */
export const useAcademicYears = ({ includeFuture = false } = {}) => {
  const years = useQuery({
    queryKey: ACADEMIC_YEARS_KEY,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BASE_URL}hec/academic/getAcademicYears`);
        return Array.isArray(data) ? [...data].sort(newestFirst) : [];
      } catch (error) {
        console.error("Failed to load academic years:", error);
        toast.error("Failed to load academic years", { id: "academic-years-error" });
        throw error;
      }
    },
  });
  const options = useOptions();
  const current = options.data?.currentAcademicYear?.code;
  // Without the current year (options failed to load) every year is shown, as before Settings
  const waiting = !includeFuture && options.isPending;
  const data = useMemo(() => {
    if (!years.data || waiting) return undefined;
    return includeFuture || current == null ? years.data : years.data.filter((y) => Number(y.academicYearCode) <= current);
  }, [years.data, waiting, includeFuture, current]);
  return { ...years, data, isPending: years.isPending || waiting };
};

// Today's academic year, as Settings define it (in the shape of the academic year list)
export const useCurrentAcademicYear = () => {
  const query = useOptions();
  const current = query.data?.currentAcademicYear;
  return { ...query, data: current ? { id: current.id, academicYearCode: String(current.code), academicYearName: current.name } : undefined };
};
